/**
 * №13.1 (§2.4) — Invoices Place (`/app/billing/invoices`).
 *
 * List: posted invoices with an aggregate header (count · total from
 * `aggregate_entities` — never a stored roll-up, never 50-capped, №11.1.3) and a
 * bearer-safe PDF download per row. Detail (`place/invoice/:id`): frozen lines +
 * total, the PDF served from R2, AR status editable (the one un-governed field),
 * and the governed lock everywhere else — a posted invoice is corrected by a
 * credit note, not an edit (the credit-note affordance is a placeholder, §3).
 */
import { useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { mcpClient } from "../../../lib/mcp-client";
import { money } from "../../components/ledger/format";
import { showToast } from "../../components/primitives";
import { useAggregate } from "../../hooks/use-aggregate";
import { GovernedNotice } from "./GovernedNotice";
import {
  useInvoices, useInvoiceLines, useMatterInvoicePdf, useUpdateArStatus, type EntityRow,
} from "../../hooks/use-time-billing";
import { TableSubjectHead } from "./TableSubjectHead";

const AR_CHOICES = ["unsent", "sent", "part_paid", "paid"];

export function InvoicesListPage() {
  const navigate = useNavigate();
  const invoices = useInvoices();
  const posted = useMemo(() => (invoices.data ?? []).filter((i) => i.status === "posted"), [invoices.data]);
  const agg = useAggregate("invoice", { count: true, sum: "total_minor", filter: { status: "posted" } });

  const count = agg.data?.count ?? posted.length;
  const total = agg.data?.sum ?? posted.reduce((s, i) => s + ((i.total_minor as number) ?? 0), 0);

  return (
    <div className="screen">
      {/* №14.1.1-B — pagehead + card like every other Place list; the subline no
          longer leaks the verb name that computed it (`aggregate_entities`). */}
      <TableSubjectHead title="Invoices" sub={<>{count} posted · {money(total)} total</>} />
      <div className="card card--flush">
          {invoices.isLoading ? <div className="skeleton"><div className="sk-line" style={{ width: "60%" }} /><div className="sk-line" style={{ width: "40%" }} /></div>
            : posted.length === 0 ? <div className="empty-state"><h3>No posted invoices</h3><p>Assemble and post invoices on the WIP &amp; billing worksheet.</p><Link to="/app/billing/wip"><button className="btn btn--primary">Open worksheet</button></Link></div>
            : (
              <div className="table-scroll"><table><thead><tr><th>Invoice</th><th>Matter</th><th>Issued</th><th className="num">Total</th><th>AR</th><th>PDF</th></tr></thead><tbody>
                {posted.map((i) => (
                  <tr key={i.id} onClick={() => navigate(`/app/place/invoice/${i.id}`)} style={{ cursor: "pointer" }}>
                    <td className="primary">{(i.invoice_no as string) ?? i.id}</td>
                    <td>{(i.matter as string) ?? "—"}</td>
                    <td className="row-date">{(i.issued_date as string) ?? "—"}</td>
                    <td className="num money">{money((i.total_minor as number) ?? 0)}</td>
                    <td><span className={`pill pill--${(i.ar_status as string) ?? "unsent"}`}>{(i.ar_status as string) ?? "unsent"}</span></td>
                    <td><PdfLink invoiceId={i.id as string} /></td>
                  </tr>
                ))}
              </tbody></table></div>
            )}
      </div>
    </div>
  );
}

function PdfLink({ invoiceId }: { invoiceId: string }) {
  const pdf = useMatterInvoicePdf(invoiceId);
  const [busy, setBusy] = useState(false);
  const fileId = pdf.data?.pdf_file_id;
  if (!fileId) return <span className="muted tiny">pending</span>;
  return (
    <button className="drill" style={{ border: "none", background: "none", cursor: "pointer" }}
      disabled={busy}
      onClick={async (e) => { e.stopPropagation(); setBusy(true); try { await mcpClient.downloadFile(fileId); } catch (err) { showToast((err as Error).message, "error"); } finally { setBusy(false); } }}>
      {busy ? "…" : "Download →"}
    </button>
  );
}

export function InvoiceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const invoices = useInvoices();
  const inv = useMemo(() => (invoices.data ?? []).find((i) => i.id === id) as (EntityRow & Record<string, unknown>) | undefined, [invoices.data, id]);
  const lines = useInvoiceLines(id);
  const pdf = useMatterInvoicePdf(id);
  const updateAr = useUpdateArStatus();
  const [busy, setBusy] = useState(false);

  if (invoices.isLoading) return <div className="screen"><div className="card"><div className="skeleton sk-line" style={{ width: "40%" }} /></div></div>;
  // The shell's Back is above this (a drill-down always gets one), so this is not a
  // second way out — it is the LIST, named as the list. It read "Back to invoices",
  // which is the same defect as the workflow pages' removed link: a FORWARD link
  // wearing a back label. Kept rather than removed because a not-found state with no
  // affordance is a dead end, and a real destination is worth naming.
  if (!inv) return <div className="screen"><div className="empty-state"><h3>Invoice not found</h3><Link to="/app/billing/invoices"><button>View all invoices</button></Link></div></div>;

  const fileId = pdf.data?.pdf_file_id;
  const total = (inv.total_minor as number) ?? 0;

  return (
    <div className="screen vt">
      <div className="surface" style={{ marginBottom: 14 }}>
        <div className="surface__bar"><div className="ledger-identity">
          <span className="statutory">{(inv.invoice_no as string) ?? inv.id} — Tax Invoice</span>
          <span className="bank">{(inv.matter as string) ?? ""} · issued {(inv.issued_date as string) ?? "—"}</span>
        </div><span className="spacer" style={{ flex: 1 }} /><span className="badge badge--finance">{(inv.status as string) ?? "posted"}</span></div>
      </div>

      <div className="card"><div className="card__h">Actions</div>
        <div className="card__b actionbar">
          <button className="act" disabled={!fileId || busy} onClick={async () => { if (!fileId) return; setBusy(true); try { await mcpClient.downloadFile(fileId); } catch (e) { showToast((e as Error).message, "error"); } finally { setBusy(false); } }}>{fileId ? "Download PDF" : "PDF pending"}</button>
          <button className="act" disabled style={{ opacity: 0.55 }}>Issue credit note <span className="prov">placeholder</span></button>
          <span className="picker" style={{ marginLeft: 8 }}><span className="muted">AR status:</span>
            <select value={(inv.ar_status as string) ?? "unsent"} disabled={updateAr.isPending}
              onChange={async (e) => { try { await updateAr.mutateAsync({ invoice_id: inv.id as string, ar_status: e.target.value }); showToast("AR status updated", "success"); } catch (err) { showToast((err as Error).message, "error"); } }}>
              {AR_CHOICES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </span>
        </div>
        <div className="card__b" style={{ borderTop: "0.5px solid var(--color-border-secondary)" }}>
          <GovernedNotice message="Lines and total are frozen on a posted invoice." fix="Correct with a credit note, not an edit" />
        </div>
      </div>

      <div className="card card--flush">
        <div className="card__h">Frozen lines <span className="sub">copied from the draft at post</span></div>
        <table className="ledger-table"><thead><tr><th>Description</th><th className="num">Amount</th></tr></thead><tbody>
          {(lines.data ?? []).map((l) => <tr key={l.id}><td>{(l.description as string) ?? "—"}</td><td className="num money">{money((l.amount_minor as number) ?? 0)}</td></tr>)}
          <tr className="total-row"><td>Total due</td><td className="num money">{money(total)}</td></tr>
        </tbody></table>
      </div>

      <div className="card"><div className="card__b"><div className="dotline tiny muted">
        <span className={`pill ${fileId ? "pill--issued" : "pill--pending"}`}><span className="dot" />PDF</span>
        {fileId ? <>rendered on post via the 10.3 renderer stack → R2 · served at <code>/files/{fileId}/download</code>{pdf.data?.pdf_sha256 ? " · sha256 stamped" : ""}.</> : "renders on post via the 10.3 stack (dev-deploy only) — pending on this environment."}
      </div></div></div>
    </div>
  );
}
